import React, { useState, useContext, useEffect, createContext, useMemo } from 'react';
import {
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineHome,
  HiOutlineTag,
  HiOutlineCube,
  HiOutlineUserGroup,
  HiOutlineChartBar,
  HiOutlineUserCircle,
  HiOutlineCog,
  HiOutlineClipboardList,
  HiOutlineLogout,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineShoppingBag,
  HiOutlineBell,
  HiOutlineExclamationCircle,
} from 'react-icons/hi';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import useStore from '../store/index';
import { isAdminUser } from '../utils/access';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: <HiOutlineHome size={20} /> },
  { to: '/products', label: 'Products', icon: <HiOutlineCube size={20} /> },
  {
    label: 'Skin Assessment',
    icon: <HiOutlineClipboardList size={20} />,
    subItems: [
      { to: '/questions', label: 'Questions', icon: <HiOutlineClipboardList size={16} /> },
      { to: '/questionnaire', label: 'Questionnaire', icon: <HiOutlineClipboardList size={16} /> },
    ],
  },
  {
    label: 'Education Info',
    icon: <HiOutlineCog size={20} />,
    subItems: [
      { to: '/education/card-types', label: 'Card Types', icon: <HiOutlineCog size={16} /> },
      { to: '/education/rule-types', label: 'Rule Types', icon: <HiOutlineCog size={16} /> },
      { to: '/education/placements', label: 'Placements', icon: <HiOutlineCog size={16} /> },
      { to: '/education/rules', label: 'Rules', icon: <HiOutlineCog size={16} /> },
      { to: '/education/cards', label: 'Cards', icon: <HiOutlineCog size={16} /> },
      { to: '/education/tags', label: 'Tags', icon: <HiOutlineCog size={16} /> },
      { to: '/education/media-types', label: 'Media Types', icon: <HiOutlineCog size={16} /> },
      { to: '/education/groups', label: 'Groups', icon: <HiOutlineCog size={16} /> },
    ],
  },
  { to: '/brands', label: 'Brands', icon: <HiOutlineTag size={20} /> },
  { to: '/retailers/stores', label: 'Retailers', icon: <HiOutlineShoppingBag size={20} /> },
  { to: '/orders', label: 'Orders', icon: <HiOutlineClipboardList size={20} /> },
  { to: '/users', label: 'Users', icon: <HiOutlineUserGroup size={20} /> },
  {
    to: '/notifications',
    label: 'Notifications',
    icon: <HiOutlineBell size={20} />,
    adminOnly: true,
  },
  {
    to: '/logs',
    label: 'Error Logs',
    icon: <HiOutlineExclamationCircle size={20} />,
    adminOnly: true,
  },
  { to: '#', label: 'Reports', icon: <HiOutlineChartBar size={20} /> },
];

const Layout = () => {
  const { logout, user } = useStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMin, setSidebarMin] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useContext(ThemeContext);
  const canAccessAdminModules = isAdminUser(user);

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => !item.adminOnly || canAccessAdminModules),
    [canAccessAdminModules]
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
    setProfileDropdownOpen(false);
  };

  const toggleMenu = (menuLabel) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuLabel]: !prev[menuLabel],
    }));
  };

  const getNavClass = (to) => ({ isActive }) => {
    // Placeholder "#" links must never look active (avoids gold border on every stub item)
    const active = Boolean(isActive && to && to !== '#');
    return `nav-item flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] leading-tight transition ${
      active ? 'nav-item-active' : 'text-white/85 hover:bg-white/10 hover:text-white'
    } ${sidebarMin ? 'justify-center' : 'justify-start'}`;
  };

  const renderNavItem = (item) => {
    if (item.subItems) {
      const isExpanded = expandedMenus[item.label];
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleMenu(item.label)}
            className={`nav-item w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] leading-tight text-white/85 hover:bg-white/10 transition ${
              sidebarMin ? 'justify-center' : 'justify-start'
            }`}
          >
            {item.icon}
            {!sidebarMin && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {isExpanded ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
              </>
            )}
          </button>
          {!sidebarMin && isExpanded && (
            <div className="ml-2.5 mt-0.5 space-y-0.5 border-l border-white/15 pl-1.5">
              {item.subItems.map((subItem) => (
                <NavLink
                  key={`${subItem.label}-${subItem.to}`}
                  to={subItem.to}
                  end={subItem.to !== '#'}
                  className={getNavClass(subItem.to)}
                  onClick={(e) => {
                    if (subItem.to === '#') e.preventDefault();
                    setSidebarOpen(false);
                  }}
                >
                  {subItem.icon}
                  <span>{subItem.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/dashboard'}
        className={getNavClass(item.to)}
        onClick={(e) => {
          if (item.to === '#') e.preventDefault();
          setSidebarOpen(false);
        }}
      >
        {item.icon}
        <span className={`${sidebarMin ? 'hidden' : 'block'} transition-all`}>{item.label}</span>
      </NavLink>
    );
  };

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-dropdown')) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);

  return (
    <div className={`flex h-screen bg-brand-mesh bg-background dark:bg-background-dark transition-colors duration-300 ${theme}`}>
      <div
        className={`fixed inset-0 z-40 bg-brand-charcoal/40 backdrop-blur-[2px] transition-opacity md:hidden ${
          sidebarOpen ? 'block' : 'hidden'
        }`}
        onClick={() => setSidebarOpen(false)}
      />
      <aside
        className={`fixed z-50 top-0 left-0 h-full bg-sidebar-brand text-text-light flex flex-col transition-all duration-200 ${
          sidebarMin ? 'w-16' : 'w-56'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:block`}
      >
        <div className="flex items-center justify-between h-12 px-2.5 border-b border-white/10">
          <div className={`${sidebarMin ? 'hidden' : 'block'}`}>
            <p className="font-sans font-semibold text-sm tracking-tight">CraftMySkin</p>
            <p className="text-[10px] uppercase tracking-[0.14em] text-brand-gold">Admin Console</p>
          </div>
          <button className="md:hidden text-white/80 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <HiOutlineX size={22} />
          </button>
          <button
            className="hidden md:block text-white/80 hover:text-white"
            onClick={() => setSidebarMin((v) => !v)}
            aria-label="Toggle sidebar"
          >
            <HiOutlineMenu size={22} />
          </button>
        </div>
        <nav className="flex-1 flex flex-col gap-0.5 mt-2 px-1.5 overflow-y-auto pb-3">
          {visibleNavItems.map(renderNavItem)}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-12 bg-white/85 dark:bg-secondary backdrop-blur-md border-b border-brand-light/40 shadow-sm flex items-center justify-between px-2.5 sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <button className="md:hidden text-brand-charcoal" onClick={() => setSidebarOpen(true)}>
              <HiOutlineMenu size={22} />
            </button>
            <span className="font-sans font-semibold text-sm md:hidden text-primary">CraftMySkin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="rounded-full p-2 bg-brand-blush/50 border border-brand-light/60 text-primary hover:bg-brand-gold/20 transition"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.95 7.05l-.71-.71M4.05 4.05l-.71-.71M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z" />
                </svg>
              )}
            </button>
            <div className="relative profile-dropdown">
              <button
                className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-brand-blush/40 focus:outline-none transition-colors"
                onClick={() => setProfileDropdownOpen((prev) => !prev)}
                aria-label="Toggle profile menu"
              >
                <HiOutlineUserCircle size={26} className="text-brand-charcoal dark:text-white" />
                <span className="hidden sm:block text-brand-charcoal dark:text-white font-medium text-sm capitalize">
                  {user?.fullName}
                </span>
              </button>
              <div
                className={`absolute right-0 mt-2 w-44 bg-white dark:bg-secondary rounded-xl border border-brand-light/40 shadow-glow py-1.5 transition-all z-50 ${
                  profileDropdownOpen
                    ? 'opacity-100 pointer-events-auto translate-y-0'
                    : 'opacity-0 pointer-events-none -translate-y-2'
                }`}
              >
                <NavLink
                  to="/profile"
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-brand-blush/40 transition-colors"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <HiOutlineUserCircle size={18} /> Profile
                </NavLink>
                <NavLink
                  to="/settings"
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-brand-blush/40 transition-colors"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <HiOutlineCog size={18} /> Settings
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 w-full text-left text-sm hover:bg-brand-blush/40 transition-colors"
                >
                  <HiOutlineLogout size={18} /> Logout
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-1.5 md:p-2 text-text dark:text-text-light">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export { ThemeContext };
export default Layout;
